'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { getBills } from '@/lib/bills';
import { getAccountBalances } from '@/lib/accountBalances';
import { DollarSign, AlertTriangle, CheckCircle2, Wifi, TrendingDown } from 'lucide-react';

interface DashboardLiveSummaryProps {}

export default function DashboardLiveSummary(_props: DashboardLiveSummaryProps) {
  const { user } = useAuth();
  const [billsTotal, setBillsTotal] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [totalCash, setTotalCash] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [bills, accounts] = await Promise.all([
        getBills(user.id),
        getAccountBalances(user.id),
      ]);
      const unpaidBills = bills.filter(b => b.bill_status !== 'Paid' && b.is_active);
      setBillsTotal(unpaidBills.reduce((s, b) => s + b.monthly_amount, 0));
      setOverdueCount(bills.filter(b => b.bill_status === 'Overdue').length);
      const cash = accounts
        .filter(a => ['checking', 'savings', 'business', 'emergency', 'tax'].includes(a.accountType))
        .reduce((s, a) => s + a.balance, 0);
      setTotalCash(cash);
      setLastUpdated(new Date());
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Auto-update on bills or balances change
  useEffect(() => {
    if (!user) return;
    const billsCh = supabase
      .channel('dash-bills-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bills', filter: `user_id=eq.${user.id}` }, load)
      .subscribe();
    const balCh = supabase
      .channel('dash-balances-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'account_balances', filter: `user_id=eq.${user.id}` }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(billsCh);
      supabase.removeChannel(balCh);
    };
  }, [user, load]);

  const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
          <DollarSign size={16} className="text-success" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-500">Total Cash</p>
          <p className="text-lg font-700 text-foreground">{fmt(totalCash)}</p>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-danger/10 flex items-center justify-center flex-shrink-0">
          <TrendingDown size={16} className="text-danger" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-500">Bills Due This Month</p>
          <p className="text-lg font-700 text-foreground">{fmt(billsTotal)}</p>
        </div>
      </div>
      <div className={`bg-card border rounded-xl p-4 flex items-center gap-3 ${overdueCount > 0 ? 'border-danger/30' : 'border-border'}`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${overdueCount > 0 ? 'bg-danger/10' : 'bg-success/10'}`}>
          {overdueCount > 0 ? <AlertTriangle size={16} className="text-danger" /> : <CheckCircle2 size={16} className="text-success" />}
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground font-500">Overdue Bills</p>
          <p className={`text-lg font-700 ${overdueCount > 0 ? 'text-danger' : 'text-success'}`}>
            {overdueCount > 0 ? `${overdueCount} overdue` : 'All clear'}
          </p>
        </div>
      </div>
      {lastUpdated && (
        <div className="sm:col-span-3 flex items-center gap-1.5 text-xs text-success/70">
          <Wifi size={10} />
          Live · auto-updates when bills or balances change · {lastUpdated.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
